<template>
  <div class="sign-up">
    <div class="sign-up__card">
      <div class="sign-up__brand">
        <img class="sign-up__logo" :src="$brand.logo" :alt="$brand.name" />
        <div>
          <h1 class="sign-up__brand-name">{{ $brand.name }}</h1>
          <p class="sign-up__brand-tag">{{ $t('shell.workspace') }}</p>
        </div>
      </div>

      <h2 class="sign-up__title">{{ $t('signup-view.title') }}</h2>

      <ValidationObserver v-slot="{ invalid, handleSubmit }">
        <form @submit.prevent="handleSubmit(signUp)" novalidate>
          <div class="sign-up__row">
            <OmxField
                v-model="name.first"
                :label="$t('signup-view.firstname-field')"
                :placeholder="$t('signup-view.firstname-placeholder')"
                rules="required"
                required
                tabindex="1"
                @enter="handleSubmit(signUp)"
            />
            <OmxField
                v-model="name.last"
                :label="$t('signup-view.lastname-field')"
                :placeholder="$t('signup-view.lastname-placeholder')"
                rules="required"
                required
                tabindex="2"
                @enter="handleSubmit(signUp)"
            />
          </div>

          <OmxField
              v-model="email"
              type="email"
              :label="$t('signup-view.email-field')"
              :placeholder="$t('signup-view.email-placeholder')"
              icon="email-outline"
              rules="required|email"
              required
              autocomplete="email"
              tabindex="3"
              @enter="handleSubmit(signUp)"
          />
          <OmxField
              v-model="password"
              type="password"
              :label="$t('signup-view.password-field')"
              :placeholder="$t('signup-view.password-placeholder')"
              icon="lock-outline"
              rules="required"
              required
              autocomplete="new-password"
              tabindex="4"
              @enter="handleSubmit(signUp)"
          />

          <b-button
              native-type="submit"
              type="is-primary"
              :loading="isLoading"
              :disabled="invalid || isLoading"
              expanded
              size="is-medium"
              tabindex="5"
          >{{ $t('signup-view.signup-button') }}</b-button>

          <p class="sign-up__login">
            {{ $t('signup-view.back-to-login-text-part1') }}
            <router-link to="/login">{{ $t('signup-view.back-to-login-text-part2') }}</router-link>
          </p>
        </form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import firebase from "firebase/app";
import { ValidationObserver } from "vee-validate";
import OmxField from "@/components/OmxField.vue";

export default {
  name: "signup",
  components: { ValidationObserver, OmxField },
  data() {
    return {
      name: { first: "", last: "" },
      email: "",
      password: "",
      isLoading: false
    };
  },
  methods: {
    signUp() {
      this.isLoading = true;
      firebase.auth()
          .createUserWithEmailAndPassword(this.email, this.password)
          .then(() => {
            this.createUser();
            this.sendVerificationEmail();
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError("Oops. " + error.message);
          });
    },
    sendVerificationEmail() {
      firebase.auth().currentUser.sendEmailVerification()
          .then(() => {
            this.showSuccess(this.$t("user-verification.verification-email-sent"));
            this.isLoading = false;
            this.$omxAlert({
              title: this.$t('user-verification.title').toString(),
              message: this.$t('user-verification.description').toString() + " " + this.email,
              type: 'is-info',
              confirmText: this.$t('common.ok').toString()
            });
            firebase.auth().signOut();
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(error.message);
          });
    },
    createUser() {
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const insertUserFunction = functions.httpsCallable(this.getSubmitEndpoint());
      insertUserFunction({
        action: "insert_user",
        name: this.name,
        email: this.email,
        roles: ["inactive"]
      }).catch((error) => {
        this.showError(error.message);
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.sign-up {
  min-height: 100vh;
  background:
      radial-gradient(circle at 90% 0%, rgba(255, 175, 55, .18), transparent 45%),
      radial-gradient(circle at 0% 100%, rgba(54, 146, 219, .14), transparent 50%),
      $omx-bg;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
}

.sign-up__card {
  width: 100%;
  max-width: 480px;
  background: $omx-surface;
  border-radius: $omx-radius-xl;
  box-shadow: $omx-shadow-lg;
  border: 1px solid $omx-ink-100;
  padding: 32px;
}

.sign-up__brand {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 22px;
}
.sign-up__logo {
  width: 38px; height: 38px;
  border-radius: 10px;
  object-fit: contain;
  background: #fff;
  padding: 3px;
}
.sign-up__brand-name {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: $omx-ink-900;
}
.sign-up__brand-tag {
  margin: 0;
  font-size: 12px;
  color: $omx-ink-500;
}

.sign-up__title {
  margin: 0 0 22px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -.012em;
  color: $omx-ink-900;
}

.sign-up__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
@media (max-width: 540px) {
  .sign-up__row { grid-template-columns: 1fr; }
}

.sign-up__login {
  margin: 18px 0 0;
  font-size: 13px;
  color: $omx-ink-500;
  text-align: center;
  a {
    color: $omx-brand-600;
    font-weight: 600;
    &:hover { color: $omx-brand-700; }
  }
}
</style>
